import { useRichTextEditor } from '@levitate/rich-text';
import type { Editor } from '@tiptap/core';
import { Extension, getMarkRange } from '@tiptap/core';
import { Highlight } from '@tiptap/extension-highlight';
import { Mention } from '@tiptap/extension-mention';
import { TextAlign } from '@tiptap/extension-text-align';
import { Color, FontSize, TextStyle } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extensions';
import { PluginKey } from '@tiptap/pm/state';
import type { SuggestionProps } from '@tiptap/suggestion';
import { Suggestion } from '@tiptap/suggestion';
import * as React from 'react';
import { ISupportSavedReply } from '../../../../models/Support/ISupportSavedReply';
import { ResizableImage } from './resizableImage';
import type { SuggestionKind } from './types';

/**
 * Callbacks the composer supplies to the editor. They are read through a ref on every invocation,
 * so the latest render's closures are always used even though the extensions are only built once.
 */
export interface ComposerLinkClickDetails {
	href: string;
	range: { from: number; to: number };
	viewportX: number;
	viewportY: number;
}

export interface ComposerEditorCallbacks {
	onEditorReady?: (editor: Editor | null) => void;
	onLinkClick: (details: ComposerLinkClickDetails) => void;
	onUpdate: (editor: Editor) => void;
	getPlaceholder: () => string;
	/** Mentions only trigger in internal notes. */
	isNoteMode: () => boolean;
	insertSavedReply: (editor: Editor, range: { from: number; to: number }, reply: ISupportSavedReply) => void;
	/** A typed `/` or `@` opened or moved a suggestion session; null props mean the session ended. */
	onSuggestionChange: (kind: SuggestionKind, props: SuggestionProps | null) => void;
	/** Return true to consume the key (menu navigation) instead of letting the editor handle it. */
	onSuggestionKeyDown: (kind: SuggestionKind, event: KeyboardEvent) => boolean;
	/** Pasted or dropped files: images land inline once uploaded; other files join the attachment list. */
	insertFiles: (files: File[], insertAt?: number) => void;
	/** Tab in the body. Return true once focus has moved to the formatting bar. */
	focusFormattingBar: () => boolean;
}

interface SlashCommandsOptions {
	getCallbacks: () => ComposerEditorCallbacks;
}

/** `/` saved-reply commands, wired through the same suggestion plumbing the mention extension uses. */
const SlashCommands = Extension.create<SlashCommandsOptions>({
	name: 'supportSlashCommands',

	addOptions() {
		return {
			getCallbacks: () => {
				throw new Error('SlashCommands requires getCallbacks');
			},
		};
	},

	addProseMirrorPlugins() {
		const getCallbacks = this.options.getCallbacks;
		return [
			Suggestion<ISupportSavedReply, ISupportSavedReply>({
				editor: this.editor,
				pluginKey: new PluginKey('supportSlashCommands'),
				char: '/',
				items: () => [],
				command: ({ editor, range, props }) => {
					getCallbacks().insertSavedReply(editor, range, props);
				},
				render: () => ({
					onStart: props => getCallbacks().onSuggestionChange('slash', props),
					onUpdate: props => getCallbacks().onSuggestionChange('slash', props),
					onKeyDown: ({ event }) => getCallbacks().onSuggestionKeyDown('slash', event),
					onExit: () => getCallbacks().onSuggestionChange('slash', null),
				}),
			}),
		];
	},
});

interface TabToFormattingBarOptions {
	getCallbacks: () => ComposerEditorCallbacks;
}

/**
 * Tab out of the body lands on the formatting bar, not the next DOM stop (Send), so a stray Tab + Enter
 * can't send. The lowest priority lets list indenting and suggestion picking claim Tab first.
 */
const TabToFormattingBar = Extension.create<TabToFormattingBarOptions>({
	name: 'supportTabToFormattingBar',
	// TipTap reads priority with `|| 100`, so 0 would silently become the default.
	priority: 1,

	addOptions() {
		return {
			getCallbacks: () => {
				throw new Error('TabToFormattingBar requires getCallbacks');
			},
		};
	},

	addKeyboardShortcuts() {
		return { Tab: () => this.options.getCallbacks().focusFormattingBar() };
	},
});

/**
 * Creates the composer's TipTap editor: rich text with links, lists, colors, sizes, highlight and
 * alignment (the formatting-bar surface), plus `@` mention and `/` saved-reply suggestions.
 */
export const useComposerEditor = (callbacks: ComposerEditorCallbacks, initialHtml: string): Editor | null => {
	const callbacksRef = React.useRef(callbacks);
	callbacksRef.current = callbacks;

	return useRichTextEditor({
		initialContent: initialHtml,
		starterKitOptions: {
			blockquote: false,
			code: false,
			codeBlock: false,
			heading: false,
			horizontalRule: false,
			link: {
				openOnClick: false,
				autolink: true,
				defaultProtocol: 'https',
			},
		},
		extensions: [
			Placeholder.configure({
				placeholder: () => callbacksRef.current.getPlaceholder(),
			}),
			TextStyle,
			Color,
			FontSize,
			Highlight.configure({ multicolor: true }),
			TextAlign.configure({ types: ['paragraph'] }),
			ResizableImage.configure({ inline: false, allowBase64: false }),
			Mention.configure({
				HTMLAttributes: { class: 'support-composer-mention' },
				suggestion: {
					char: '@',
					allow: () => callbacksRef.current.isNoteMode(),
					items: () => [],
					render: () => ({
						onStart: props => callbacksRef.current.onSuggestionChange('mention', props),
						onUpdate: props => callbacksRef.current.onSuggestionChange('mention', props),
						onKeyDown: ({ event }) => callbacksRef.current.onSuggestionKeyDown('mention', event),
						onExit: () => callbacksRef.current.onSuggestionChange('mention', null),
					}),
				},
			}),
			SlashCommands.configure({
				getCallbacks: () => callbacksRef.current,
			}),
			TabToFormattingBar.configure({
				getCallbacks: () => callbacksRef.current,
			}),
		],
		mergeField: false,
		editorProps: {
			attributes: { class: 'support-composer-content' },
			handleDOMEvents: {
				mouseover: (view, event) => {
					const target = event.target instanceof Element ? event.target.closest('a[href]') : null;
					const href = target?.getAttribute('href');
					if (target && href && view.dom.contains(target)) target.setAttribute('title', href);
					return false;
				},
			},
			handleClick: (view, position, event) => {
				const target = event.target instanceof Element ? event.target.closest('a[href]') : null;
				const href = target?.getAttribute('href');
				const linkMark = view.state.schema.marks.link;
				const range = linkMark ? getMarkRange(view.state.doc.resolve(position), linkMark) : undefined;
				if (!target || !href || !range || !view.dom.contains(target)) return false;

				event.preventDefault();
				const rect = target.getBoundingClientRect();
				callbacksRef.current.onLinkClick({
					href,
					range,
					viewportX: rect.left,
					viewportY: rect.bottom,
				});
				return true;
			},
			handlePaste: (_view, event) => {
				const files = Array.from(event.clipboardData?.files ?? []);
				if (files.length === 0) return false;
				event.preventDefault();
				callbacksRef.current.insertFiles(files);
				return true;
			},
			handleDrop: (view, event, _slice, moved) => {
				// An internal drag of existing content is a move; let ProseMirror handle it.
				if (moved) return false;
				const files = Array.from(event.dataTransfer?.files ?? []);
				if (files.length === 0) return false;
				event.preventDefault();
				const dropPos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
				callbacksRef.current.insertFiles(files, dropPos);
				return true;
			},
		},
		onCreate: editor => callbacksRef.current.onEditorReady?.(editor),
		onDestroy: () => callbacksRef.current.onEditorReady?.(null),
		onUpdate: editor => callbacksRef.current.onUpdate(editor),
		shouldRerenderOnTransaction: false,
	});
};

/** User IDs of the teammates currently mentioned in the editor document (note mode only). */
export const getMentionedUserIds = (editor: Editor | null): string[] => {
	if (!editor) return [];
	const ids = new Set<string>();
	editor.state.doc.descendants(node => {
		if (node.type.name === 'mention' && typeof node.attrs.id === 'string' && node.attrs.id) {
			ids.add(node.attrs.id);
		}
	});
	return [...ids];
};
